import React, { useCallback, useEffect, useState, useRef } from "react";
import classnames from "classnames";
import PropTypes from "prop-types";
import "./multiRangeSlider.css";

const NMultiRangeSlider = ({ min, max, initialMin, initialMax, onChange }) => {
  const [minVal, setMinVal] = useState(initialMin);
  const [maxVal, setMaxVal] = useState(initialMax);
  const minValRef = useRef(null);
  const maxValRef = useRef(null);
  const range = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPos, setTooltipPos] = useState(null);
  const [showTooltipMax, setShowTooltipMax] = useState(false);
  const [tooltipPosMax, setTooltipPosMax] = useState(null);

  useEffect(() => {
    setMinVal(initialMin);
  }, [initialMin]);

  useEffect(() => {
    setMaxVal(initialMax);
  }, [initialMax]);

  const getPercent = useCallback(
    (value) => Math.round(((value - min) / (max - min)) * 100),
    [min, max]
  );

  useEffect(() => {
    if (maxValRef.current) {
      const minPercent = getPercent(minVal);
      const maxPercent = getPercent(+maxValRef.current.value);
      if (range.current) {
        range.current.style.left = `${minPercent}%`;
        range.current.style.width = `${maxPercent - minPercent}%`;
      }
    }
  }, [minVal, getPercent]);

  useEffect(() => {
    if (minValRef.current) {
      const minPercent = getPercent(+minValRef.current.value);
      const maxPercent = getPercent(maxVal);
      if (range.current) {
        range.current.style.width = `${maxPercent - minPercent}%`;
      }
    }
  }, [maxVal, getPercent]);
  const updateTooltipPosition = (value) => {
    const range = minValRef.current;
    if (!range) return;

    const minRange = Number(range.min || 0);
    const maxRange = Number(range.max || 100);
    const percent = (value - minRange) / (maxRange - minRange);

    const rangeWidth = range.offsetWidth;
    const thumbOffset = percent * rangeWidth;
    setTooltipPos(thumbOffset);
  };
  const updateTooltipPositionMax = (value) => {
    const range = maxValRef.current;
    if (!range) return;

    const minRange = Number(range.min || 0);
    const maxRange = Number(range.max || 100);
    const percent = (value - minRange) / (maxRange - minRange);

    const rangeWidth = range.offsetWidth;
    const thumbOffset = percent * rangeWidth;
    setTooltipPosMax(thumbOffset);
  };
  return (
    <div className="d-flex align-items-center">
      <div className="slider__left-value">{min}</div>
      <div style={{ marginTop: "-6px", width: "100%", position: "relative" }}>
        <div style={{ position: "relative" }}>
          <input
            type="range"
            min={min}
            max={max}
            value={minVal}
            ref={minValRef}
            onChange={(event) => {
              const value = Math.min(+event.target.value, parseInt(maxVal) - 1);
              setMinVal(value);
              event.target.value = value.toString();
              updateTooltipPosition(value);
            }}
            onMouseUp={() => {
              onChange({ min: minVal, max: maxVal });
            }}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onTouchEnd={() => {
              onChange({ min: minVal, max: maxVal });
            }}
            className={classnames("thumb thumb--zindex-3", {
              "thumb--zindex-5": minVal > max - 100,
            })}
          />
          {showTooltip && tooltipPos && (
            <div
              style={{
                position: "absolute",
                top: "-35px",
                left: `${tooltipPos}px`,
                transform: "translateX(-50%)",
                zIndex: 10,
                backgroundColor: "black",
                color: "white",
                padding: "4px 8px",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            >
              {minVal}
            </div>
          )}
        </div>
        <div>
          <input
            type="range"
            min={min}
            max={max}
            value={maxVal}
            ref={maxValRef}
            onChange={(event) => {
              const value = Math.max(+event.target.value, parseInt(minVal) + 1);
              setMaxVal(value);
              event.target.value = value.toString();
              updateTooltipPositionMax(value);
            }}
            onMouseEnter={() => setShowTooltipMax(true)}
            onMouseLeave={() => setShowTooltipMax(false)}
            onMouseUp={() => onChange({ min: minVal, max: maxVal })}
            onTouchEnd={() => onChange({ min: minVal, max: maxVal })}
            className="thumb thumb--zindex-4"
          />
          {showTooltipMax && tooltipPosMax && (
            <div
              style={{
                position: "absolute",
                top: "-35px",
                left: `${tooltipPosMax}px`,
                transform: "translateX(-50%)",
                zIndex: 10,
                backgroundColor: "black",
                color: "white",
                padding: "4px 8px",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            >
              {maxVal}
            </div>
          )}
        </div>
        <div className="slider">
          <div className="slider__track" />
          <div ref={range} className="slider__range" />
        </div>
      </div>
      <div className="slider__right-value" style={{ minWidth: "30px" }}>
        {max}
      </div>
    </div>
  );
};

NMultiRangeSlider.propTypes = {
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default NMultiRangeSlider;
